/**
 * @file Products.jsx
 * @description Page de gestion des produits de l'application
 * 
 * Cette page gère l'affichage et la manipulation des produits avec les fonctionnalités suivantes :
 * 
 * 1. Gestion des données
 *    - Chargement initial automatique via useEffect
 *    - Rechargement automatique après les opérations CRUD
 *    - Gestion des états de chargement et d'erreur
 *    - Pagination et filtrage côté client
 * 
 * 2. Configuration du tableau
 *    - Colonnes personnalisables avec tri
 *    - Filtres primaires et secondaires
 *    - Actions sur les lignes et en-tête
 *    - Support multilingue
 * 
 * 3. Gestion des états
 *    - État de chargement (loading)
 *    - Gestion des erreurs
 *    - Pagination (page courante, items par page)
 *    - Filtres et recherche
 *    - Configuration du tri
 * 
 * 4. Fonctionnalités de rafraîchissement
 *    - Rechargement manuel via refreshData
 *    - Rechargement automatique après CRUD
 *    - Réinitialisation de la pagination lors des changements
 * 
 * 5. Internationalisation
 *    - Support multilingue (FR, ES, AR)
 *    - Traductions des libellés
 *    - Format des nombres et dates
 * 
 * @example
 * // Utilisation du composant
 * <Products />
 * 
 * @example
 * // Configuration du tableau
 * const tableConfig = {
 *   pagination: { ... },
 *   sorting: { ... }
 * };
 * 
 * @example
 * // Configuration des colonnes
 * const columns = [
 *   {
 *     key: 'name',
 *     label: 'Nom',
 *     sortable: true,
 *     visible: true
 *   }
 * ];
 * 
 * @example
 * // Configuration des filtres
 * const filterConfig = {
 *   primary: [
 *     {
 *       type: 'select',
 *       key: 'category',
 *       label: 'Catégorie',
 *       options: [...]
 *     }
 *   ]
 * };
 * 
 * @example
 * // Gestion du rechargement des données
 * const refreshData = useCallback(() => {
 *   setRefreshTrigger(prev => prev + 1);
 * }, []);
 * 
 * @example
 * // Chargement automatique des données
 * useEffect(() => {
 *   fetchData({
 *     page: currentPage,
 *     limit: itemsPerPage,
 *     search,
 *     filters,
 *     sortConfig
 *   });
 * }, [fetchData, currentPage, itemsPerPage, search, filters, sortConfig, refreshTrigger]);
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useLanguage } from '../../../../../hooks/useLanguage';
import { products as frProducts } from '../../../../../locales/fr/products';
import { products as esProducts } from '../../../../../locales/es/products';
import { products as arProducts } from '../../../../../locales/ar/products';
import { ProductService } from '../../../../../services/AppDataServices/services/product.service';
import { CategoryService } from '../../../../../services/AppDataServices/services/category.service';
import DataTable from '../../../components/DataTable';
import FilterSection from '../../../components/DataTable/FilterSection';
import useSearch from '../../../hooks/useSearch';
import PrintableView from '../../../components/DataTable/PrintableView';
import Modal from '../../../components/common/Modal';
import ProductForm from './ProductForm';
import { aside as frAside } from '../../../../../locales/fr/aside';
import { aside as arAside } from '../../../../../locales/ar/aside';
import { aside as esAside } from '../../../../../locales/es/aside';
import useMyAppStore from '../../../../../stores/myAppStore';
import { formatDate } from '../../../../../utils';

const translations = {
  fr: frProducts,
  es: esProducts,
  ar: arProducts
};

const asideTranslations = {
  fr: frAside,
  ar: arAside,
  es: esAside
};

const Products = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const asideT = asideTranslations[lang];
  const { setPageTitle } = useMyAppStore();
  const productService = new ProductService();
  const categoryService = new CategoryService();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // État pour la pagination et les filtres
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [showSecondary, setShowSecondary] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });

  // Configuration de la recherche
  const searchConfig = useMemo(() => ({
    minMatchLength: 2,
    fields: [
      { key: 'name', type: 'text' },
      { key: 'barcode', type: 'text' },
      { key: 'category', type: 'text' },
      { key: 'price', type: 'currency' },
      { key: 'stock', type: 'number' }
    ]
  }), []);

  const { searchItems } = useSearch(searchConfig);

  // Handlers pour les filtres
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  // Handler pour le changement de catégorie
  const handleCategoryChange = useCallback((value) => {
    
    const selectedCategory = categories.find(cat => cat.value === value);
    
    setFilters(prev => {
      const newFilters = { ...prev, category: value };
      
      return newFilters;
    });
    setCurrentPage(1);
  }, [categories]);

  const handleTypeChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, type: value }));
    setCurrentPage(1);
  }, []);

  const handleStockStatusChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, stockStatus: value }));
    setCurrentPage(1);
  }, []);

  const toggleSecondary = useCallback(() => {
    setShowSecondary(prev => !prev);
  }, []);

  // Configuration des filtres primaires
  const primaryFilters = useMemo(() => [
    {
      type: 'search',
      key: 'search',
      label: t.filters.search.label,
      placeholder: t.filters.search.placeholder,
      value: search,
      onChange: handleSearchChange,
      useAzerty: true
    },
    {
      type: 'sort',
      key: 'sort',
      label: t.filters.sort?.label || 'Trier par',
      value: sortConfig.key,
      direction: sortConfig.direction,
      onChange: (value) => {
        setSortConfig(prev => {
          const newConfig = {
            key: value,
            direction: prev.key === value && prev.direction === 'asc' ? 'desc' : 'asc'
          };
          return newConfig;
        });
      },
      options: [
        { value: 'name', label: t.columns.name },
        { value: 'barcode', label: t.columns.barcode },
        { value: 'category', label: t.columns.category },
        { value: 'price', label: t.columns.price },
        { value: 'stock', label: t.columns.stock }
      ]
    }
  ], [t, search, handleSearchChange, sortConfig]);

  // Configuration des filtres secondaires
  const secondaryFilters = useMemo(() => {
    
    return [
      {
        type: 'select',
        key: 'category',
        label: t.filters.category.label,
        value: filters.category || '',
        onChange: handleCategoryChange,
        options: categories,
        loading: categoriesLoading
      },
      {
        type: 'select',
        key: 'type',
        label: t.filters.type.label,
        value: filters.type,
        onChange: handleTypeChange,
        options: [
          { value: 'product', label: t.filters.type.product },
          { value: 'service', label: t.filters.type.service }
        ]
      },
      {
        type: 'select',
        key: 'stockStatus',
        label: t.filters.stockStatus.label,
        value: filters.stockStatus,
        onChange: handleStockStatusChange,
        options: [
          { value: 'in-stock', label: t.filters.stockStatus.inStock },
          { value: 'low-stock', label: t.filters.stockStatus.lowStock },
          { value: 'out-of-stock', label: t.filters.stockStatus.outOfStock }
        ]
      }
    ];
  }, [t, filters, handleCategoryChange, handleTypeChange, handleStockStatusChange, categories, categoriesLoading]);

  // Handlers génériques pour le DataTable
  const handleSearch = useCallback((searchTerm) => {
    setSearch(searchTerm);
    setCurrentPage(1);
  }, []);

  // Handler générique pour les changements de filtres
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  // Fonction pour forcer le rechargement des données
  const refreshData = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Configuration du tableau
  const tableConfig = useMemo(() => ({
    pagination: {
      pageSize: 10,
      pageSizeOptions: [10, 25, 50, 100]
    },
    sorting: {
      defaultSort: { key: 'name', direction: 'asc' }
    }
  }), []);

  // Configuration des colonnes
  const columns = useMemo(() => [
    {
      key: 'barcode',
      label: t.columns.barcode,
      sortable: true,
      visible: true
    },
    {
      key: 'categoryName',
      label: t.columns.category,
      sortable: true,
      visible: true
    },
    {
      key: 'name',
      label: t.columns.name,
      sortable: true,
      visible: true
    },
    {
      key: 'price',
      label: t.columns.price,
      sortable: true,
      visible: true,
      render: (item) => item.price.toFixed(2)
    },
    {
      key: 'stock',
      label: t.columns.stock,
      sortable: true,
      visible: true,
      render: (item) => (
        <span className={item.stock <= 10 ? 'text-red-500' : ''}>
          {item.stock}
        </span>
      )
    },
    {
      key: 'Type',
      label: t.columns.isService,
      sortable: true,
      visible: true,
      render: (item) => {
        const typeLabels = {
          'product': t.filters.type.product || 'Produit',
          'service': t.filters.type.service || 'Service',
          'pack': t.filters.type.pack || 'Pack',
          'matiere_premiere': t.filters.type.matiere_premiere || 'Matière première',
          'piece_detachee': t.filters.type.piece_detachee || 'Pièce détachée'
        };
        return typeLabels[item.Type] || item.Type || t.common.notDefined || 'Non défini';
      }
    }
  ], [t]);

  // Configuration des colonnes collapsibles
  const collapsibleColumns = useMemo(() => [
    {
      key: 'basicInfo',
      label: t.columns.basicInfo || 'Informations de base',
      render: (item) => (
        <dl className="mt-2 space-y-2">
          {item.image && (
            <div className="flex items-center gap-2">
              <dt className="text-gray-500 min-w-[120px]">{t.columns.image || 'Image'}</dt>
              <dd className="flex-1">
                <img 
                  src={item.image} 
                  alt={item.name} 
                  className="w-16 h-16 object-cover rounded-lg"
                />
              </dd>
            </div>
          )}
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.barcode || 'Code-barres'}</dt>
            <dd className="flex-1">{item.barcode || 'Non disponible'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.isService || 'Type'}</dt>
            <dd className="flex-1">
              {(() => {
                const typeLabels = {
                  'product': t.filters.type.product || 'Produit',
                  'service': t.filters.type.service || 'Service',
                  'pack': t.filters.type.pack || 'Pack',
                  'matiere_premiere': t.filters.type.matiere_premiere || 'Matière première',
                  'piece_detachee': t.filters.type.piece_detachee || 'Pièce détachée'
                };
                return typeLabels[item.Type] || item.Type || t.common.notDefined || 'Non défini';
              })()}
            </dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.status || 'Statut'}</dt>
            <dd className="flex-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {item.isActive ? 'Actif' : 'Inactif'}
              </span>
            </dd>
          </div>
        </dl>
      )
    },
    {
      key: 'financialInfo',
      label: t.columns.financialInfo || 'Détails financiers',
      render: (item) => (
        <dl className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.price || 'Prix de vente'}</dt>
            <dd className="flex-1">{item.price ? `${item.price.toFixed(2)} Dh` : 'Non disponible'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.cost || 'Coût'}</dt>
            <dd className="flex-1">{item.cost ? `${item.cost.toFixed(2)} Dh` : 'Non disponible'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.tauxTva || 'TVA'}</dt>
            <dd className="flex-1">{item.tauxTva ? `${item.tauxTva}%` : 'Non disponible'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.unite || 'Unité'}</dt>
            <dd className="flex-1">{item.unite || 'Non définie'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.stock || 'Stock'}</dt>
            <dd className="flex-1">
              {item.Type !== 'service' ? (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  item.stock > 10 ? 'bg-green-100 text-green-800' :
                  item.stock > 0 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {item.stock} unités
                </span>
              ) : 'N/A'}
            </dd>
          </div>
        </dl>
      )
    },
    {
      key: 'systemInfo',
      label: t.columns.systemInfo || 'Informations système',
      render: (item) => (
        <dl className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.createdAt || 'Date de création'}</dt>
            <dd className="flex-1">{formatDate(item.createdAt)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.updatedAt || 'Dernière modification'}</dt>
            <dd className="flex-1">{formatDate(item.updatedAt)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.lastSync || 'Dernière synchronisation'}</dt>
            <dd className="flex-1">{formatDate(item.lastSync)}</dd>
          </div>
        </dl>
      )
    },
    {
      key: 'packItems',
      label: t.columns.packItems || 'Composition du pack',
      render: (item) => {
        if (!item.packItems || item.packItems.length === 0) {
          return (
            <div className="mt-2 text-gray-500">
              {item.Type === 'pack' ? 'Aucun élément dans la composition' : 'Non applicable'}
            </div>
          );
        }
        
        return (
          <div className="mt-2 space-y-2">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Éléments de composition ({item.packItems.length} élément{item.packItems.length > 1 ? 's' : ''})
            </div>
            {item.packItems.map((packItem, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{packItem.name}</div>
                  {packItem.barcode && (
                    <div className="text-sm text-gray-500">Code: {packItem.barcode}</div>
                  )}
                </div>
                <div className="text-sm font-medium text-gray-700 ml-4">
                  Qté: {packItem.quantity}
                </div>
              </div>
            ))}
          </div>
        );
      }
    }
  ], [t]);

  // Configuration des filtres
  const filterConfig = useMemo(() => ({
    primary: [
      {
        type: 'search',
        key: 'search',
        label: t.filters.search.label,
        placeholder: t.filters.search.placeholder
      }
    ],
    secondary: [
      {
        type: 'select',
        key: 'category',
        label: t.filters.category.label,
        options: categories
      },
      {
        type: 'select',
        key: 'type',
        label: t.filters.type.label,
        options: [
          { value: 'product', label: t.filters.type.product },
          { value: 'service', label: t.filters.type.service }
        ]
      },
      {
        type: 'select',
        key: 'stockStatus',
        label: t.filters.stockStatus.label,
        options: [
          { value: 'in-stock', label: t.filters.stockStatus.inStock },
          { value: 'low-stock', label: t.filters.stockStatus.lowStock },
          { value: 'out-of-stock', label: t.filters.stockStatus.outOfStock }
        ]
      }
    ]
  }), [t, filters, handleCategoryChange, categories]);

  // Fonction de récupération des données
  const fetchData = useCallback(async ({ page, limit, search, filters, sortConfig }) => {
    try {
      setLoading(true);
      setError(null);
      
      let products = await productService.getAll();
      let total = products.length;
     

      // Application de la recherche avancée
      if (search) {
        products = searchItems(search, products);
      }

      // Application des filtres
      if (filters.category) {        
        // Filtrer directement par categoryId
        products = products.filter(p => p.categoryId == filters.category);
        // console.log('products', products);
        
      }

      if (filters.type) {
        products = products.filter(p => p.isService === (filters.type === 'service'));
      }

      if (filters.stockStatus) {
        switch (filters.stockStatus) {
          case 'in-stock':
            products = products.filter(p => p.stock > 10);
            break;
          case 'low-stock':
            products = products.filter(p => p.stock <= 10 && p.stock > 0);
            break;
          case 'out-of-stock':
            products = products.filter(p => p.stock === 0);
            break;
        }
      }

      // Appliquer le tri
      if (sortConfig.key) {
        products.sort((a, b) => {
          const aValue = a[sortConfig.key];
          const bValue = b[sortConfig.key];
          const direction = sortConfig.direction === 'asc' ? 1 : -1;
          
          if (typeof aValue === 'string') {
            return direction * aValue.localeCompare(bValue);
          }
          return direction * (aValue - bValue);
        });
      }

      // Mettre à jour le total après tous les filtres
      total = products.length;

      // Pagination
      const start = (page - 1) * limit;
      const end = start + limit;
      products = products.slice(start, end);

      setData({ items: products, total });
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [searchItems, categories]);

  // Effet pour charger les données
  useEffect(() => {
    // // console.log('Effect triggered with filters:', filters); // Debug
    fetchData({
      page: currentPage,
      limit: itemsPerPage,
      search,
      filters,
      sortConfig
    });
  }, [fetchData, currentPage, itemsPerPage, search, filters, sortConfig, refreshTrigger]);

  // Handlers pour les changements d'état
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Réinitialiser à la première page
  }, []);

  const handleSortChange = useCallback((newSortConfig) => {
    setSortConfig(newSortConfig);
  }, []);

  // Configuration des actions
  const actionConfig = useMemo(() => ({
    primary: [
      {
        label: t.actions.newProduct,
        onClick: () => {
          setSelectedProduct(null);
          setIsEditMode(false);
          setIsModalOpen(true);
        },
        primary: true
      }
    ],
    row: [
      {
        icon: 'edit',
        onClick: (item) => {
          setSelectedProduct(item);
          setIsEditMode(true);
          setIsModalOpen(true);
        },
        title: t.form.editTitle,
        className: 'text-blue-500 hover:text-blue-700'
      },
      {
        icon: 'delete',
        onClick: (item) => {
          if (window.confirm(t.confirmations.delete)) {
            productService.delete(item.id);
            refreshData();
          }
        },
        title: t.actions.delete,
        className: 'text-red-500 hover:text-red-700'
      }
    ]
  }), [t, refreshData]);

  // Configuration complète pour le DataTable
  const dataTableConfig = useMemo(() => ({
    tableConfig,
    columns,
    filters: filterConfig,
    actions: actionConfig,
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    totalItems: data.total,
    onPageChange: handlePageChange,
    onItemsPerPageChange: handleItemsPerPageChange,
    onSearch: handleSearch,
    onSortChange: handleSortChange,
    onFilterChange: handleFilterChange,
    refreshData,
    collapsibleColumns
  }), [
    tableConfig,
    columns,
    filterConfig,
    actionConfig,
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    handleSearch,
    handleSortChange,
    handleFilterChange,
    refreshData,
    collapsibleColumns
  ]);

  // Chargement des catégories
  const loadCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const categories = await categoryService.getAll();
      // // console.log('Loaded categories:', categories); // Debug des catégories chargées
      const formattedCategories = categories.map(cat => ({
        value: cat.id.toString(), // Assurer que l'ID est une chaîne
        label: cat.name
      }));
      // // console.log('Formatted categories:', formattedCategories); // Debug des catégories formatées
      setCategories(formattedCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError(error);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Effet pour charger les catégories
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false); // Mode création ou édition
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', type: 'info' });

  // Handlers pour les opérations CRUD
  const handleAddProduct = () => {
    setSelectedProduct(null);
    setIsEditMode(false); // Mode création
    setIsModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setIsEditMode(true); // Mode édition
    setIsModalOpen(true);
  };

  const handleDeleteProduct = async (product) => {
    if (window.confirm(t.confirmations.delete)) {
      try {
        await productService.delete(product.id);
        refreshData();
      } catch (error) {
        setError(error);
      }
    }
  };

  const handleSubmitProduct = async (formData) => {
    try {
      setIsSubmitting(true);
      setAlert({ open: true, message: 'En cours de traitement...', type: 'info' });
      
      if (isEditMode) {
        await productService.update(selectedProduct.id, formData);
        setAlert({ open: true, message: 'Produit modifié avec succès !', type: 'success' });
      } else {
        // Créer le produit et récupérer le produit créé
        const createdProduct = await productService.create(formData);
        setAlert({ open: true, message: 'Produit créé avec succès !', type: 'success' });
        
        // Passer en mode édition avec le produit créé
        setSelectedProduct(createdProduct);
        setIsEditMode(true); // Passer en mode édition
      }
      refreshData();
      
      // Masquer le message de succès après 3 secondes
      setTimeout(() => setAlert({ open: false, message: '', type: 'info' }), 3000);
    } catch (error) {
      setAlert({ open: true, message: `Erreur : ${error.message}`, type: 'error' });
      console.error('Erreur lors de la soumission du produit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour remettre en mode création
  const handleResetToCreate = () => {
    setSelectedProduct(null);
    setIsEditMode(false); // Retour en mode création
  };

  useEffect(() => {
    setPageTitle(asideT.routes.products);
  }, [lang, asideT.routes.products, setPageTitle]);

  return (
    <>
      <div className="space-y-4 print-hide">
        <FilterSection
          primaryFilters={primaryFilters}
          secondaryFilters={secondaryFilters}
          onSearch={handleSearchChange}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          onAdd={() => {
            setSelectedProduct(null);
            setIsEditMode(false);
            setIsModalOpen(true);
          }}
        />
        <DataTable {...dataTableConfig} />
      </div>
      <PrintableView
        title={t.title}
        columns={columns}
        data={data.items}
        translations={t}
      />
      
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditMode ? t.form.editTitle : t.form.createTitle}
        width="max-w-xl"
      >
        <ProductForm
          product={selectedProduct}
          isEditMode={isEditMode}
          categories={categories}
          products={data.items}
          onSubmit={handleSubmitProduct}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
          alert={alert}
          onAlertClose={() => setAlert({ open: false, message: '', type: 'info' })}
          onResetToCreate={handleResetToCreate}
        />
      </Modal>
    </>
  );
};

export default Products; 